import React, { createContext, useContext, useState } from 'react';
import { AsyncData } from '@Lib/models';
import {
  Product,
  FeatItem,
  FeatDescrip,
  RevisionHistory,
  Release,
  PromptVersionControl,
  JiraProject,
  JiraIssue,
  IssueDescrip,
} from '@Models/types';

import { ahaAPI } from '@Lib/axios/aha';
import { jiraAPI } from '@Lib/axios/jira';
import { useAuth } from './CognitoAuthProvider';
import { aiGenerateAPI, testCasesAPI } from '@Lib/axios/testcases';

interface GeneratorContextProps {
  isJiraMode: boolean;
  setJiraMode: (mode: boolean) => void;
  // AHA Values
  products: Product[];
  fetchProducts: () => Promise<void>;
  selectedProduct: Product | null;
  setSelectedProduct: (product: Product | null) => void;
  releases: Release[];
  fetchReleases: () => Promise<void>;
  selectedRelease: Release | null;
  setSelectedRelease: React.Dispatch<React.SetStateAction<Release | null>>;
  featItems: FeatItem[];
  fetchFeatItems: (product: Product) => Promise<void>;
  selectedFeatItem: FeatItem | null;
  setSelectedFeatItem: (featItem: FeatItem | null) => void;
  featDescrips: FeatDescrip[];
  fetchFeatDescrips: (referenceNum: string) => Promise<void>;
  selectedRequirements: FeatDescrip[];
  setSelectedRequirements: React.Dispatch<React.SetStateAction<FeatDescrip[]>>;
  updateRequirementDescription: (
    requirementId: string,
    description: string,
    context?: string
  ) => void;

  // Shared Values
  clearAllContexts: () => void;
  generateTestCases: (
    requirements: FeatDescrip[] | IssueDescrip[]
  ) => Promise<AsyncData>;
  generateStatus: string;
  setGenerateStatus: (status: string) => void;
  getRevisionHistory: (id: string) => Promise<RevisionHistory>;
  regenerateTestCases: (
    id: string,
    instructions: string,
    requirements: FeatDescrip[] | IssueDescrip[] //new union
  ) => Promise<AsyncData | null>;
  genPageLoading: boolean;
  setGenPageLoading: React.Dispatch<React.SetStateAction<boolean>>;
  generateAhaLoading: boolean;
  setGenerateAhaLoading: React.Dispatch<React.SetStateAction<boolean>>;
  generatedAhaTicket: string | null;
  setGeneratedAhaTicket: React.Dispatch<React.SetStateAction<string | null>>;
  generateAhaTicket: (prompt: string) => Promise<void>;
  publishedAhaLink: string | null;
  setPublishedAhaLink: React.Dispatch<React.SetStateAction<string | null>>;
  selectedPrompt: PromptVersionControl | null;
  setSelectedPrompt: React.Dispatch<
    React.SetStateAction<PromptVersionControl | null>
  >;

  //Jira Values
  jiraProjects: JiraProject[];
  fetchJiraProjects: () => Promise<void>;
  selectedJiraProject: JiraProject | null;
  setSelectedJiraProject: (project: JiraProject | null) => void;
  jiraIssues: JiraIssue[];
  fetchJiraIssues: (projectKey: string) => Promise<void>;
  selectedJiraIssue: JiraIssue | null;
  setSelectedJiraIssue: (issue: JiraIssue | null) => void;
  issueDescrips: IssueDescrip[];
  fetchIssueDescrips: (issueKey: string) => Promise<void>;
  //generateJiraIssue: (prompt: string) => Promise<void>;
  selectedSubtasks: IssueDescrip[];
  setSelectedSubtasks: React.Dispatch<React.SetStateAction<IssueDescrip[]>>;
  updateSubtaskDescription: (
    subtaskId: string,
    description: string,
    context?: string
  ) => void;
}

const GeneratorContext = createContext<GeneratorContextProps | undefined>(
  undefined
);

export const GeneratorContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [isJiraMode, setJiraMode] = useState(false);

  // Shared states
  const [selectedPrompt, setSelectedPrompt] =
    useState<PromptVersionControl | null>(null);
  const [generateStatus, setGenerateStatus] = useState('No Pending Tasks');
  const [generatedAhaTicket, setGeneratedAhaTicket] = useState<string | null>(
    null
  );
  const [generateAhaLoading, setGenerateAhaLoading] = useState(false);
  const [publishedAhaLink, setPublishedAhaLink] = useState<string | null>(null);
  const [genPageLoading, setGenPageLoading] = useState(false);

  // Aha States
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [featItems, setFeatItems] = useState<FeatItem[]>([]);
  const [selectedFeatItem, setSelectedFeatItem] = useState<FeatItem | null>(
    null
  );
  const [selectedRequirements, setSelectedRequirements] = useState<
    FeatDescrip[]
  >([]);
  const [featDescrips, setFeatDescrips] = useState<FeatDescrip[]>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);

  // Jira States
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraProject, setSelectedJiraProject] =
    useState<JiraProject | null>(null);
  const [jiraIssues, setJiraIssues] = useState<JiraIssue[]>([]);
  const [selectedJiraIssue, setSelectedJiraIssue] = useState<JiraIssue | null>(
    null
  );
  const [issueDescrips, setIssueDescrips] = useState<IssueDescrip[]>([]);
  const [selectedSubtasks, setSelectedSubtasks] = useState<IssueDescrip[]>([]);

  const { logout } = useAuth();
  const aha = ahaAPI(logout);
  const jira = jiraAPI(logout);
  const testcases = testCasesAPI(logout);
  const aiGenerate = aiGenerateAPI(logout);

  const generateAhaTicket = async (prompt: string) => {
    setGenerateAhaLoading(true);
    try {
      const ticket = await aiGenerate.generateAhaTicket(prompt);
      setGeneratedAhaTicket(ticket);
    } catch (error) {
      console.error('Error generating Aha! ticket:', error);
      setGeneratedAhaTicket(null);
    } finally {
      setGenerateAhaLoading(false);
    }
  };

  const fetchReleases = async () => {
    if (!selectedProduct) return;
    const releases = await aha.getReleases(selectedProduct.reference_prefix);
    setReleases(releases || []);
  };

  const fetchProducts = async () => {
    const products = await aha.getProd();
    setProducts(products || []);
  };

  const fetchFeatItems = async (product: Product) => {
    const features = await aha.getFeatList(product.reference_prefix);
    setFeatItems(features || []);
  };

  const fetchFeatDescrips = async (referenceNum: string) => {
    const requirements = await aha.getFeatDetails(referenceNum);
    setFeatDescrips(requirements || []);
  };

  const updateRequirementDescription = (
    requirementId: string,
    description: string,
    context?: string
  ) => {
    // Update Aha state
    setFeatDescrips((prevDescrips) =>
      prevDescrips.map((req) =>
        req.id === requirementId
          ? { ...req, description_body: description, context: context }
          : req
      )
    );

    // Update Selected state
    setSelectedRequirements((prevSelected) =>
      prevSelected.map((req) =>
        req.id === requirementId
          ? { ...req, description_body: description, context: context }
          : req
      )
    );
  };

  // Jira Functions
  const fetchJiraProjects = async () => {
    const projects = await jira.getProjects();
    setJiraProjects(projects || []);
  };

  const fetchJiraIssues = async (projectKey: string) => {
    const issues = await jira.getIssueList(projectKey);
    setJiraIssues(issues || []);
  };

  const fetchIssueDescrips = async (issueKey: string) => {
    const issue = await jira.getIssueDetails(issueKey);
    if (issue) {
      // Map JiraIssue to IssueDescrip format
      const descrip: IssueDescrip = {
        id: issue.id,
        key: issue.key,
        summary: issue.summary,
        description_body: issue.description || '', // Transform description to description_body
        context: undefined,
        attachment: issue.attachment,
      };
      setIssueDescrips([descrip]);
    } else {
      setIssueDescrips([]);
    }
  };

  const updateSubtaskDescription = (
    subtaskId: string,
    description: string,
    context?: string
  ) => {
    // Update Jira state
    setIssueDescrips((prevDescrips) =>
      prevDescrips.map((req) =>
        req.id === subtaskId
          ? { ...req, description_body: description, context } // Use shorthand
          : req
      )
    );
    // Update Selected state
    setSelectedSubtasks((prevSelected) =>
      prevSelected.map((subtask) =>
        subtask.id === subtaskId
          ? { ...subtask, description_body: description, context: context }
          : subtask
      )
    );
  };

  //Shared logic

  const clearAllContexts = () => {
    //aha
    setFeatDescrips((prevDescrips) =>
      prevDescrips.map((req) => ({ ...req, context: undefined }))
    );
    setSelectedRequirements((prevSelected) =>
      prevSelected.map((req) => ({ ...req, context: undefined }))
    );

    //jira
    setIssueDescrips((prevDescrips) =>
      prevDescrips.map((subtask) => ({ ...subtask, context: undefined }))
    );
    setSelectedSubtasks((prevSelected) =>
      prevSelected.map((subtask) => ({ ...subtask, context: undefined }))
    );
  };

  const generateTestCases = async (
    requirements: FeatDescrip[] | IssueDescrip[]
  ) => {
    //new union in requirements param
    const body = {
      email: '',
      requirements,
      selectedPrompt: selectedPrompt?.content || undefined,
    };
    const queue = (await aiGenerate.generate(body)) as AsyncData;
    return queue;
  };

  const regenerateTestCases = async (
    id: string,
    instructions: string,
    requirements: FeatDescrip[] | IssueDescrip[] //new union
  ) => {
    const body = {
      instructions,
      requirements,
      historyId: id,
      selectedPrompt: selectedPrompt?.content || undefined,
    };
    try {
      const queue = (await aiGenerate.reGenerate(body)) as AsyncData;
      return queue;
    } catch (error) {
      return null;
    }
  };

  const getRevisionHistory = async (id: string) => {
    const revision = await testcases.fetchRevisionHistory(id);
    return revision;
  };

  return (
    <GeneratorContext.Provider
      value={{
        isJiraMode,
        setJiraMode,
        releases,
        fetchReleases,
        selectedRelease,
        setSelectedRelease,
        products,
        fetchProducts,
        selectedProduct,
        setSelectedProduct,
        featItems,
        fetchFeatItems,
        selectedFeatItem,
        setSelectedFeatItem,
        featDescrips,
        fetchFeatDescrips,
        updateRequirementDescription,
        clearAllContexts,
        selectedRequirements,
        setSelectedRequirements,
        generateTestCases,
        generateStatus,
        setGenerateStatus,
        getRevisionHistory,
        regenerateTestCases,
        genPageLoading,
        setGenPageLoading,
        generateAhaLoading,
        setGenerateAhaLoading,
        generatedAhaTicket,
        setGeneratedAhaTicket,
        generateAhaTicket,
        publishedAhaLink,
        setPublishedAhaLink,
        selectedPrompt,
        setSelectedPrompt,

        // Jira
        jiraProjects,
        fetchJiraProjects,
        selectedJiraProject,
        setSelectedJiraProject,
        jiraIssues,
        fetchJiraIssues,
        selectedJiraIssue,
        setSelectedJiraIssue,
        issueDescrips,
        fetchIssueDescrips,
        setSelectedSubtasks,
        selectedSubtasks,
        updateSubtaskDescription,
      }}
    >
      {children}
    </GeneratorContext.Provider>
  );
};
// eslint-disable-next-line react-refresh/only-export-components
export const useGeneratorContext = (): GeneratorContextProps => {
  const context = useContext(GeneratorContext);
  if (!context)
    throw new Error('useGeneratorContext must be used within a GenCtxProvider');
  return context;
};
